/**
 * ─────────────────────────────────────────────────────────────
 * SITE CONFIG — static, developer-owned settings.
 *
 * This is the compile-time layer: brand, contact, currency, and
 * *default* opening hours / services. Things the business owner needs
 * to change without a redeploy live in the editable content layer
 * instead (see config/content.registry.ts + lib/content.ts).
 *
 * Rule of thumb:
 *   - changes rarely, set by the developer  → here
 *   - changes often, edited by the owner     → content collection
 * ─────────────────────────────────────────────────────────────
 */
import type { OpeningHour } from '@/lib/types';

export interface SiteConfig {
  name: string;
  legalName: string;
  tagline: string;
  description: string;
  /** Absolute URL of the deployed site (overridden by NEXT_PUBLIC_SITE_URL). */
  url: string;
  locale: string;

  contact: {
    email: string;
    phone: string;
    address: string;
    city: string;
    postalCode: string;
    country: string;
    geo?: { lat: number; lng: number };
  };

  social: {
    facebook?: string;
    instagram?: string;
    twitter?: string;
  };

  /** Brand palette — fed straight into the MUI theme. */
  brand: {
    primary: string;
    secondary: string;
    mode: 'light' | 'dark';
  };

  /** Default opening hours. The owner can override these live via the
   *  `hours` content slot; use resolveHours() to read the effective value. */
  openingHours: OpeningHour[];

  /** Appointment booking defaults. */
  booking: {
    slotMinutes: number;
    horizonDays: number;
    services: string[];
  };

  /** Currency for product prices (ISO 4217). */
  currency: string;
}

export const siteConfig: SiteConfig = {
  name: 'LC Coiffure',
  legalName: 'LC Coiffure — Camille Lavoye',
  tagline: 'Styliste · Visagiste · Conseil',
  description:
    'LC Coiffure, salon de coiffure à Vizille : coupes femme et enfant, coloration, balayage, cheveux bouclés, brushing. Styliste-visagiste, sur rendez-vous au 04 76 40 93 29.',
  url: process.env.NEXT_PUBLIC_SITE_URL || 'https://www.lc-coiffure.fr',
  locale: 'fr_FR',

  contact: {
    email: '',
    phone: '+33 4 76 40 93 29',
    address: '76 avenue Aristide Briand',
    city: 'Vizille',
    postalCode: '38220',
    country: 'FR',
    geo: { lat: 45.0753621, lng: 5.7706986 },
  },

  social: {},

  brand: {
    // Accent terracotta — le fil de mèche, les CTA. Réservé à cela (brief § Palette).
    primary: '#B4623F',
    // Sauge profonde — réservée au bandeau Coiffeurs Justes.
    secondary: '#5C6B52',
    mode: 'light',
  },

  // Horaires relevés sur la fiche en ligne (Fresha/Google) : fermé lundi et
  // dimanche, pause méridienne 12 h–14 h, samedi matin uniquement.
  // À confirmer par la propriétaire — modifiable en direct via le slot `hours`.
  openingHours: [
    { day: 2, open: '09:00', close: '12:00' },
    { day: 2, open: '14:00', close: '18:00' },
    { day: 3, open: '09:00', close: '12:00' },
    { day: 3, open: '14:00', close: '18:00' },
    { day: 4, open: '09:00', close: '12:00' },
    { day: 4, open: '14:00', close: '18:00' },
    { day: 5, open: '09:00', close: '12:00' },
    { day: 5, open: '14:00', close: '18:00' },
    { day: 6, open: '09:00', close: '12:00' },
  ],

  booking: {
    slotMinutes: 30,
    horizonDays: 30,
    services: [
      'Coupe femme',
      'Coupe enfant',
      'Coloration / balayage',
      'Cheveux bouclés',
      'Brushing / coiffage',
      'Lissage / soin kératine',
      'Extensions',
      'Conseil visagiste / autre',
    ],
  },

  currency: 'EUR',
};

export default siteConfig;

